/**
 * Featured Collection Tabs
 * Handles tab switching and dynamic product loading for recommendations and recently viewed
 */

const RECENTLY_VIEWED_KEY = "recently-viewed-products";
const RECENTLY_VIEWED_MAX = 20;

if (!customElements.get("featured-collection-tabs")) {
  customElements.define(
    "featured-collection-tabs",
    class FeaturedCollectionTabs extends HTMLElement {
      constructor() {
        super();
        this.tabButtons = [];
        this.tabContents = [];
        this.currentTab = null;
        this.sectionId = this.dataset.sectionId;
        this.externalNavPrev = null;
        this.externalNavNext = null;
        this.activeSlider = null;
        this.boundSwiperEvents = null;
        this.swiperCheckInterval = null;
        this.emptyTabs = new Set();
        this.pendingTabs = new Set();
      }

      connectedCallback() {
        this.tabButtons = this.querySelectorAll("[data-tab]");
        this.tabContents = this.querySelectorAll("[data-tab-content]");

        // Find external navigation arrows
        this.externalNavPrev = this.querySelector(
          "[data-external-nav-prev]",
        );
        this.externalNavNext = this.querySelector(
          "[data-external-nav-next]",
        );

        if (this.tabButtons.length > 0) {
          this.addEventListeners();

          // Set initial active tab
          const activeButton = this.querySelector("[data-tab].active");
          if (activeButton) {
            this.currentTab = activeButton.dataset.tab;
          }
          // Initialize external nav for active tab
          if (this.externalNavPrev && this.externalNavNext) {
            this.initExternalNav();
            // Bind to initial active slider - start polling immediately
            this.bindToActiveSlider();

            // Also use MutationObserver as backup for detecting slider changes
            this.setupSliderObserver();
          }
        } else if (this.tabContents.length > 0) {
          // Single block mode - no tab buttons, set current tab from content
          const activeContent = this.querySelector(
            "[data-tab-content].active",
          );
          if (activeContent) {
            this.currentTab = activeContent.dataset.tabContent;
          }
        }

        // Listen for slider replacement events (from recommendations/recently-viewed)
        this.handleSliderReplaced =
          this.handleSliderReplaced.bind(this);
        this.addEventListener(
          "slider:replaced",
          this.handleSliderReplaced,
        );

        // Listen for slider:empty events (from recommendations/recently-viewed)
        this.handleSliderEmpty = this.handleSliderEmpty.bind(this);
        this.addEventListener("slider:empty", this.handleSliderEmpty);

        // Check for empty tabs
        const isDynamic = !!this.querySelector(
          "product-recommendations, recently-viewed-products",
        );
        if (!isDynamic) {
          this.checkEmptyTabs();
        } else {
          // Populate pendingTabs for tabs containing async components
          this.tabContents.forEach((content) => {
            if (
              content.querySelector(
                "product-recommendations, recently-viewed-products",
              )
            ) {
              this.pendingTabs.add(content.dataset.tabContent);
            }
          });
          this.setupDynamicEmptyCheck();
        }
      }

      setupSliderObserver() {
        this.sliderObserver = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            // Check if slideshow-section was added
            for (const node of mutation.addedNodes) {
              if (node.nodeName === "SLIDESHOW-SECTION") {
                // New slider added, rebind after it initializes
                setTimeout(() => this.bindToActiveSlider(), 100);
                return;
              }
            }
          }
        });

        // Observe the active tab content for child changes
        this.tabContents.forEach((content) => {
          this.sliderObserver.observe(content, {
            childList: true,
            subtree: true,
          });
        });
      }

      /**
       * For dynamic mode: watch tab contents for DOM changes (e.g. global.js
       * replacing product-recommendations innerHTML) and check emptiness after
       * mutations settle. Also sets a fallback timeout for silent failures.
       */
      setupDynamicEmptyCheck() {
        this.emptyCheckDebounce = null;

        this.emptyContentObserver = new MutationObserver(() => {
          // Skip mutation-based checks while tabs are still pending async data
          if (this.pendingTabs.size > 0) return;
          if (this.emptyCheckDebounce)
            clearTimeout(this.emptyCheckDebounce);
          this.emptyCheckDebounce = setTimeout(() => {
            this.checkEmptyTabs();
          }, 1000);
        });

        this.tabContents.forEach((content) => {
          this.emptyContentObserver.observe(content, {
            childList: true,
            subtree: true,
          });
        });

        // Fallback: if no mutations occur (e.g. fetch fails silently), check after timeout
        this.emptyFallbackTimeout = setTimeout(() => {
          this.pendingTabs.clear();
          this.checkEmptyTabs();
        }, 10000);
      }

      handleSliderReplaced(event) {
        const tabContent = event.target.closest(
          "[data-tab-content]",
        );
        if (tabContent) {
          const tabId = tabContent.dataset.tabContent;
          this.pendingTabs.delete(tabId);

          // Race condition recovery: if tab was previously marked empty, un-mark it
          if (this.emptyTabs.has(tabId)) {
            this.emptyTabs.delete(tabId);
            this.showTabButton(tabId);

            // If section was hidden, show it and switch to this tab
            this.showSection();
            if (
              this.currentTab === null ||
              this.emptyTabs.has(this.currentTab)
            ) {
              this.switchToTab(tabId);
            }
          }
        }

        // Rebind external nav if the replaced slider is in the active tab
        if (this.externalNavPrev && this.externalNavNext) {
          const activeContent = this.querySelector(
            `[data-tab-content="${this.currentTab}"]`,
          );
          if (activeContent?.contains(event.target)) {
            this.bindToActiveSlider();
          }
        }
      }

      initExternalNav() {
        this.externalNavPrev.addEventListener("click", () =>
          this.handleNavClick("prev"),
        );
        this.externalNavNext.addEventListener("click", () =>
          this.handleNavClick("next"),
        );

        // Keyboard support
        this.externalNavPrev.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.handleNavClick("prev");
          }
        });
        this.externalNavNext.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.handleNavClick("next");
          }
        });
      }

      handleNavClick(direction) {
        // Dynamically find the active slider to handle cases where it was replaced
        const activeContent = this.querySelector(
          `[data-tab-content="${this.currentTab}"]`,
        );
        const slider =
          activeContent?.querySelector("slideshow-section");
        const swiper = slider?.swiper;

        if (!swiper) return;

        if (direction === "prev") {
          swiper.slidePrev();
        } else {
          swiper.slideNext();
        }

        // Update nav state after navigation
        this.updateNavState(swiper);
      }

      bindToActiveSlider() {
        // Remove previous event listeners and polling
        this.unbindSwiperEvents();
        if (this.swiperCheckInterval) {
          clearInterval(this.swiperCheckInterval);
          this.swiperCheckInterval = null;
        }

        // Find the active tab content
        const activeContent = this.querySelector(
          `[data-tab-content="${this.currentTab}"]`,
        );
        if (!activeContent) return;

        // Find the slider in the active content
        const slider =
          activeContent.querySelector("slideshow-section");
        if (!slider) return;

        this.activeSlider = slider;

        // Poll for swiper initialization (it may not be ready yet on page load)
        const tryAttach = () => {
          if (slider.swiper) {
            if (this.swiperCheckInterval) {
              clearInterval(this.swiperCheckInterval);
              this.swiperCheckInterval = null;
            }
            this.attachSwiperEvents(slider.swiper);
            return true;
          }
          return false;
        };

        // Try immediately
        if (!tryAttach()) {
          // Poll until swiper is ready
          this.swiperCheckInterval = setInterval(tryAttach, 50);
          // Clear interval after 10 seconds to prevent memory leaks
          setTimeout(() => {
            if (this.swiperCheckInterval) {
              clearInterval(this.swiperCheckInterval);
              this.swiperCheckInterval = null;
            }
          }, 10000);
        }
      }

      attachSwiperEvents(swiper) {
        // Update nav state immediately
        this.updateNavState(swiper);

        // Create bound event handlers
        this.boundSwiperEvents = {
          slideChange: () => this.updateNavState(swiper),
          reachBeginning: () => this.updateNavState(swiper),
          reachEnd: () => this.updateNavState(swiper),
        };

        // Attach events
        swiper.on(
          "slideChange",
          this.boundSwiperEvents.slideChange,
        );
        swiper.on(
          "reachBeginning",
          this.boundSwiperEvents.reachBeginning,
        );
        swiper.on("reachEnd", this.boundSwiperEvents.reachEnd);
      }

      unbindSwiperEvents() {
        if (!this.activeSlider?.swiper || !this.boundSwiperEvents)
          return;

        const swiper = this.activeSlider.swiper;
        swiper.off(
          "slideChange",
          this.boundSwiperEvents.slideChange,
        );
        swiper.off(
          "reachBeginning",
          this.boundSwiperEvents.reachBeginning,
        );
        swiper.off("reachEnd", this.boundSwiperEvents.reachEnd);

        this.boundSwiperEvents = null;
      }

      updateNavState(swiper) {
        if (!this.externalNavPrev || !this.externalNavNext) return;

        const isBeginning = swiper.isBeginning;
        const isEnd = swiper.isEnd;

        // Update disabled state (using is-disabled to match existing CSS)
        this.externalNavPrev.classList.toggle(
          "is-disabled",
          isBeginning,
        );
        this.externalNavNext.classList.toggle("is-disabled", isEnd);

        // Update aria-disabled for accessibility
        this.externalNavPrev.setAttribute(
          "aria-disabled",
          isBeginning,
        );
        this.externalNavNext.setAttribute("aria-disabled", isEnd);
      }

      addEventListeners() {
        this.tabButtons.forEach((button) => {
          button.addEventListener("click", (e) =>
            this.handleTabClick(e),
          );
          button.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              this.handleTabClick(e);
            }
          });
        });
      }

      handleTabClick(event) {
        const button = event.currentTarget;
        const tabId = button.dataset.tab;

        if (tabId === this.currentTab) return;
        this.switchToTab(tabId);
      }

      switchToTab(tabId) {
        // Update button states
        this.tabButtons.forEach((btn) =>
          btn.classList.remove("active"),
        );
        const targetButton = this.querySelector(
          `[data-tab="${tabId}"]`,
        );
        if (targetButton) targetButton.classList.add("active");

        // Update content visibility
        this.tabContents.forEach((content) => {
          if (content.dataset.tabContent === tabId) {
            content.classList.add("active");
          } else {
            content.classList.remove("active");
          }
        });

        this.currentTab = tabId;

        // Reinitialize sliders in the newly visible tab
        this.reinitializeSliders(tabId);

        // Rebind external nav to the new active slider
        if (this.externalNavPrev && this.externalNavNext) {
          this.bindToActiveSlider();
        }
      }

      isTabEmpty(tabId) {
        if (this.pendingTabs.has(tabId)) return false;
        const content = this.querySelector(
          `[data-tab-content="${tabId}"]`,
        );
        if (!content) return true;
        const container = content.querySelector(
          "[data-swiper-container]",
        );
        if (!container) return true;
        return container.querySelectorAll("[data-swiper-slide]").length === 0;
      }

      getFirstNonEmptyTab() {
        for (const button of this.tabButtons) {
          const tabId = button.dataset.tab;
          if (!this.emptyTabs.has(tabId)) return tabId;
        }
        return null;
      }

      checkEmptyTabs() {
        // Clear fallback timeout if still pending
        if (this.emptyFallbackTimeout) {
          clearTimeout(this.emptyFallbackTimeout);
          this.emptyFallbackTimeout = null;
        }

        // Mark empty tabs and hide their buttons
        this.tabButtons.forEach((button) => {
          const tabId = button.dataset.tab;
          if (this.emptyTabs.has(tabId)) return; // already marked
          if (this.isTabEmpty(tabId)) {
            this.emptyTabs.add(tabId);
            this.hideTabButton(tabId);
          }
        });

        // Single block mode (no tab buttons) - check content directly
        if (this.tabButtons.length === 0) {
          this.tabContents.forEach((content) => {
            const tabId = content.dataset.tabContent;
            if (this.emptyTabs.has(tabId)) return;
            if (this.isTabEmpty(tabId)) {
              this.emptyTabs.add(tabId);
            }
          });
        }

        // If active tab is empty, switch to next or hide section
        if (
          this.currentTab &&
          this.emptyTabs.has(this.currentTab)
        ) {
          const nextTab = this.getFirstNonEmptyTab();
          if (nextTab) {
            this.switchToTab(nextTab);
          } else {
            this.hideSection();
          }
        }
      }

      handleSliderEmpty(event) {
        const tabContent = event.target.closest(
          "[data-tab-content]",
        );
        if (!tabContent) return;

        const tabId = tabContent.dataset.tabContent;
        this.pendingTabs.delete(tabId);
        this.emptyTabs.add(tabId);
        this.hideTabButton(tabId);

        if (tabId === this.currentTab) {
          const nextTab = this.getFirstNonEmptyTab();
          if (nextTab) {
            this.switchToTab(nextTab);
          } else if (this.pendingTabs.size === 0) {
            this.hideSection();
          }
        }
      }

      hideTabButton(tabId) {
        const button = this.querySelector(
          `[data-tab="${tabId}"]`,
        );
        if (button) button.style.display = "none";
      }

      hideSection() {
        if (this.pendingTabs.size > 0) return;
        // Skip self — featured-collection-tabs also has data-section-id
        const section = this.parentElement?.closest(
          "[data-section-id]",
        );
        if (section) {
          section.style.display = "none";
        }
      }

      showTabButton(tabId) {
        const button = this.querySelector(
          `[data-tab="${tabId}"]`,
        );
        if (button) button.style.display = "";
      }

      showSection() {
        const section = this.parentElement?.closest(
          "[data-section-id]",
        );
        if (section) {
          section.style.display = "";
        }
      }

      reinitializeSliders(tabId) {
        const activeContent = this.querySelector(
          `[data-tab-content="${tabId}"]`,
        );
        if (!activeContent) return;

        const sliders =
          activeContent.querySelectorAll("slideshow-section");
        sliders.forEach((slider) => {
          if (slider.swiper) {
            slider.swiper.slideTo(0, 0);
            slider.swiper.update();
          }
          window.dispatchEvent(new Event("resize"));
        });
      }

      disconnectedCallback() {
        this.unbindSwiperEvents();
        if (this.swiperCheckInterval) {
          clearInterval(this.swiperCheckInterval);
          this.swiperCheckInterval = null;
        }
        if (this.sliderObserver) {
          this.sliderObserver.disconnect();
          this.sliderObserver = null;
        }
        if (this.emptyContentObserver) {
          this.emptyContentObserver.disconnect();
          this.emptyContentObserver = null;
        }
        if (this.emptyCheckDebounce) {
          clearTimeout(this.emptyCheckDebounce);
        }
        if (this.emptyFallbackTimeout) {
          clearTimeout(this.emptyFallbackTimeout);
        }
        if (this.handleSliderReplaced) {
          this.removeEventListener(
            "slider:replaced",
            this.handleSliderReplaced,
          );
        }
        if (this.handleSliderEmpty) {
          this.removeEventListener(
            "slider:empty",
            this.handleSliderEmpty,
          );
        }
        this.tabButtons.forEach((button) => {
          button.removeEventListener("click", this.handleTabClick);
          button.removeEventListener(
            "keydown",
            this.handleTabClick,
          );
        });
      }
    },
  );
}

/**
 * Recently Viewed Products Custom Element
 * Manages localStorage for recently viewed products and renders them
 */
if (!customElements.get("recently-viewed-products")) {
  customElements.define(
    "recently-viewed-products",
    class RecentlyViewedProducts extends HTMLElement {
      constructor() {
        super();
        this.loaded = false;
        this.sectionId = this.dataset.sectionId;
        this.limit = parseInt(this.dataset.limit, 10) || 8;
        this.currentProduct = this.dataset.currentProduct;
      }

      connectedCallback() {
        this.loadRecentlyViewed();
      }

      getRecentlyViewed() {
        try {
          const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
          return stored ? JSON.parse(stored) : [];
        } catch {
          return [];
        }
      }

      async loadRecentlyViewed() {
        if (this.loaded) return;

        const handles = this.getRecentlyViewed();

        // Filter out current product and limit
        const filteredHandles = handles
          .filter((handle) => handle !== this.currentProduct)
          .slice(0, this.limit);

        if (filteredHandles.length === 0) {
          this.handleNoProducts();
          this.loaded = true;
          return;
        }

        try {
          // Fetch products via Section Rendering API
          const products =
            await this.fetchProducts(filteredHandles);

          if (products.length > 0) {
            this.renderProducts(products);
          } else {
            this.handleNoProducts();
          }

          this.loaded = true;
        } catch (error) {
          console.error(
            "Error loading recently viewed products:",
            error,
          );
          this.handleNoProducts();
          this.loaded = true;
        }
      }

      async fetchProducts(handles) {
        // Fetch each product's card HTML via the section rendering API
        const fetchPromises = handles.map(async (handle) => {
          try {
            const url = `${window.Shopify.routes.root}products/${handle}?section_id=product-card-fragment`;
            const response = await fetch(url);

            if (response.ok && !response.redirected) {
              const html = await response.text();
              // Verify the response contains actual product card content
              if (html.includes("product-card-fragment")) {
                return { handle, html };
              }
            }
          } catch {
            return null;
          }
          return null;
        });

        const results = await Promise.all(fetchPromises);
        const valid = results.filter((r) => r !== null);

        // Remove invalid handles from localStorage to avoid repeated failed fetches
        const validHandles = new Set(valid.map((r) => r.handle));
        const invalidHandles = handles.filter(
          (h) => !validHandles.has(h),
        );
        if (invalidHandles.length > 0) {
          this.removeFromStorage(invalidHandles);
        }

        return valid;
      }

      removeFromStorage(handlesToRemove) {
        try {
          const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
          if (!stored) return;
          const viewed = JSON.parse(stored);
          const removeSet = new Set(handlesToRemove);
          const filtered = viewed.filter((h) => !removeSet.has(h));
          localStorage.setItem(
            RECENTLY_VIEWED_KEY,
            JSON.stringify(filtered),
          );
        } catch {
          // Silent fail — localStorage cleanup is non-critical
        }
      }

      renderProducts(products) {
        const container = this.querySelector(
          "[data-swiper-container]",
        );
        if (!container) return;

        // Clear placeholder content
        container.innerHTML = "";

        products.forEach((product, index) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(
            product.html,
            "text/html",
          );
          const cardContent = doc.querySelector(
            ".product-card-fragment",
          );

          if (cardContent) {
            const slide = document.createElement("div");
            slide.setAttribute("data-swiper-slide", "");

            const wrapper = document.createElement("div");
            wrapper.className = "wt-slider__card-wrapper";
            wrapper.style.setProperty(
              "--animation-order",
              index + 1,
            );

            wrapper.innerHTML = cardContent.innerHTML;
            slide.appendChild(wrapper);
            container.appendChild(slide);
          }
        });

        // Reinitialize the slider
        this.initializeSlider();
      }

      handleNoProducts() {
        const slider = this.querySelector("slideshow-section");
        if (slider) {
          slider.classList.add("wt-featured-collection__empty");
        }
        this.dispatchEvent(
          new CustomEvent("slider:empty", { bubbles: true }),
        );
      }

      initializeSlider() {
        const slider = this.querySelector("slideshow-section");
        if (slider) {
          if (typeof slider.swiperDestroy === "function") {
            slider.swiperDestroy();
          }
          if (typeof slider.swiperInitilize === "function") {
            slider.swiperInitilize();
          }
        }
        window.dispatchEvent(new Event("resize"));

        // Notify parent tabs component that slider was replaced
        // Use requestAnimationFrame to ensure DOM is settled and swiper is ready
        requestAnimationFrame(() => {
          this.dispatchEvent(
            new CustomEvent("slider:replaced", { bubbles: true }),
          );
        });
      }
    },
  );
}

/**
 * Track recently viewed products
 * Call this on product pages to add to history
 */
function trackRecentlyViewed(productHandle) {
  if (!productHandle) return;

  try {
    let viewed = [];
    const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);

    if (stored) {
      viewed = JSON.parse(stored);
    }

    // Remove if already exists (to move to front)
    viewed = viewed.filter((handle) => handle !== productHandle);

    // Add to front
    viewed.unshift(productHandle);

    // Limit stored items
    viewed = viewed.slice(0, RECENTLY_VIEWED_MAX);

    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(viewed));
  } catch (error) {
    console.error("Error tracking recently viewed product:", error);
  }
}

// Auto-track on product pages
document.addEventListener("DOMContentLoaded", () => {
  const productHandle = document.querySelector(
    "[data-product-handle]",
  );
  if (productHandle) {
    trackRecentlyViewed(productHandle.dataset.productHandle);
  }
});

// Export for use elsewhere
window.trackRecentlyViewed = trackRecentlyViewed;
